'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui";
import { domId } from "@/lib/dom-id";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <ErrorBoundaryFallback
            message={this.state.error?.message}
            onReset={this.handleReset}
          />
        )
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundaryFallback({
  message,
  onReset,
}: {
  message?: string;
  onReset: () => void;
}) {
  const t = useTranslations("ErrorBoundary");

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <h1 className="text-2xl font-bold text-red-600 mb-4">{t("title")}</h1>
      <p className="text-gray-600 mb-6">
        {message || t("fallbackMessage")}
      </p>
      <Button data-testid={domId("btn", "error-boundary-try-again")} onClick={onReset}>
        {t("actions.tryAgain")}
      </Button>
    </div>
  );
}
